/**
 * Default Home Tiles - Manifest-Based Configuration
 *
 * Core apps always register tiles.
 * Dynamic apps only register tiles when installed by the user.
 *
 * This file provides backward compatibility while transitioning
 * to manifest-based tile registration via the app registry.
 *
 * Recommended pattern for new apps:
 * ```ts
 * // In your app's main.tsx
 * import { registerHomeTile } from '@room/shared-ui';
 *
 * registerHomeTile({ id: 'my-app', label: 'My App', icon: '...', route: '/my-app' });
 * ```
 */

import { registerHomeTile, registerHomeTiles, useHomeScreenStore, type HomeTile } from './homescreen';
import { useSessionStore } from './session';
import { isAdminRole } from '../utils/roles';
import { useAppRegistryStore } from './appRegistry';
import { isDevConsoleAllowed, checkDevConsoleAccess } from './devConsoleAccess';

/**
 * Core tiles - always shown on home screen.
 * These are the essential apps that make up the platform.
 */
const CORE_TILES: HomeTile[] = [
  { id: 'chat', label: 'Chat', icon: '💬', route: '/chat', section: 'dock', order: 0 },
  { id: 'store', label: 'Store', icon: '🛒', route: '/store', section: 'dock', order: 1 },
  { id: 'settings', label: 'Settings', icon: '⚙️', route: '/settings', section: 'dock', order: 2 },
  { id: 'credits', label: 'Credits', icon: '💰', route: '/credits', section: 'main', order: 4 },
  {
    id: 'developer',
    label: 'Developer',
    icon: '🧪',
    route: '/developer',
    section: 'main',
    order: 6,
    visible: () => isDevConsoleAllowed(),
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: '🛠️',
    route: '/admin',
    section: 'main',
    order: 90,
    visible: () => isAdminRole(useSessionStore.getState().user?.role),
  },
  {
    id: 'openclaw-gateway',
    label: 'Gateway',
    icon: '🌐',
    route: '/openclaw-gateway',
    section: 'main',
    order: 91,
    visible: () => isAdminRole(useSessionStore.getState().user?.role),
  },
];

/**
 * Dynamic tiles - only shown when the app is installed by the user.
 * Available in the Store for users to discover and add.
 */
const DYNAMIC_TILES: HomeTile[] = [
  { id: 'weather', label: 'Weather', icon: '🌤️', route: '/weather', section: 'main', order: 0 },
  { id: 'docs', label: 'Docs', icon: '📚', route: '/docs', section: 'main', order: 5 },
  { id: 'ai-image', label: 'AI Image', icon: '🎨', route: '/ai-image', section: 'main', order: 5 },
  { id: 'temple-run', label: 'Temple Run', icon: '🏃', route: '/temple-run', section: 'main', order: 7 },
  { id: 'flappy-bird', label: 'Flappy Bird', icon: '🐦', route: '/flappy-bird', section: 'main', order: 8 },
  { id: 'last-z', label: 'Last Z', icon: '🏃‍♂️', route: '/last-z', section: 'main', order: 9 },
  { id: 'acherun', label: 'Acherun', icon: '⚔️', route: '/acherun', section: 'main', order: 10 },
  { id: 'cadival', label: 'Cadival', icon: '🛡️', route: '/cadival', section: 'main', order: 26 },
  { id: 'skull-dash', label: 'SkullDash', icon: '💀', route: '/skull-dash', section: 'main', order: 11 },
  { id: 'ninja-fruit', label: 'Ninja Slash', icon: '🗡️', route: '/ninja-fruit', section: 'main', order: 12 },
  { id: 'dino-run', label: 'Dino Run', icon: '🦖', route: '/dino-run', section: 'main', order: 13 },
  { id: 'stack-tower', label: 'Stack Tower', icon: '🗼', route: '/stack-tower', section: 'main', order: 14 },
  { id: 'tetris-3d', label: 'Tetris 3D', icon: '🧊', route: '/tetris-3d', section: 'main', order: 15 },
  { id: 'gambling', label: 'Gambling', icon: '🎰', route: '/gambling', section: 'main', order: 16 },
  { id: 'catalog', label: 'Asset Catalog', icon: '🗂️', route: '/catalog', section: 'main', order: 17 },
  { id: 'board-game', label: 'Board Game', icon: '♟️', route: '/board-game', section: 'main', order: 18 },
  { id: 'plugins', label: 'Plugins', icon: '🧩', route: '/plugins', section: 'main', order: 19 },
  { id: 'card-game', label: 'Arcane Summons', icon: '🃏', route: '/card-game', section: 'main', order: 20 },
  { id: 'auto-chess', label: 'Arcane Tactics', icon: '♟️', route: '/auto-chess', section: 'main', order: 23 },
];

/** All tiles combined (for backward compatibility) */
const ALL_TILES: HomeTile[] = [...CORE_TILES, ...DYNAMIC_TILES];

/**
 * Fetch admin icon overrides from public config and store them in the
 * homescreen store.  The store's `setIconOverrides` will update any
 * already-registered tiles, and future `registerTile` calls will
 * automatically pick up the overrides, so late-loading bundles and
 * catalog refreshes can never revert an admin-set icon.
 */
async function applyTileIconOverrides(): Promise<void> {
  try {
    const res = await fetch('/api/config');
    if (!res.ok) return;
    const json = await res.json();
    const overrides = json?.data?.['ui.tile_icons'];
    if (!overrides || typeof overrides !== 'object') return;

    const iconMap = overrides as Record<string, string>;
    useHomeScreenStore.getState().setIconOverrides(iconMap);
  } catch {
    // Silently ignore – tiles keep their default icons
  }
}

/**
 * Register the default Room platform tiles.
 * Call this during app initialization.
 *
 * - Core tiles are always registered
 * - Dynamic tiles are ONLY registered if the user has installed them
 * - The installedApps list is persisted in localStorage by appRegistry,
 *   so it's available synchronously even before the catalog API responds
 * - loadAppCatalog() (called async) will sync with server and adjust tiles
 */
export function registerDefaultTiles(): void {
  // Always register core tiles
  registerTiles(CORE_TILES);

  // Only register dynamic tiles that user has explicitly installed
  const registryState = useAppRegistryStore.getState();
  const installedApps = registryState.installedApps;

  const installedDynamic = DYNAMIC_TILES.filter((t) =>
    installedApps.includes(t.id)
  );
  registerTiles(installedDynamic);

  // Remove any stale dynamic tiles from homescreen that are no longer installed
  // This handles the case where tiles were persisted in localStorage from before
  const uninstalledDynamic = DYNAMIC_TILES.filter((t) =>
    !installedApps.includes(t.id)
  );
  uninstalledDynamic.forEach((t) => useHomeScreenStore.getState().unregisterTile(t.id));

  // Unregister tiles for core apps that should NOT show on the grid
  // (e.g. 'home' is the launcher itself, 'login' is a flow screen, 'shell' was removed)
  const coreIdsWithTiles = new Set(CORE_TILES.map((t) => t.id));
  ['home', 'login', 'shell'].forEach((id) => {
    if (!coreIdsWithTiles.has(id)) {
      useHomeScreenStore.getState().unregisterTile(id);
    }
  });

  applyTileIconOverrides();

  // Check dev console access and re-register the developer tile to update visibility
  const devTile = CORE_TILES.find((t) => t.id === 'developer');
  checkDevConsoleAccess(devTile);
}

/**
 * Register an array of tiles at once.
 * Uses the store's batch path so the home grid only re-renders once for
 * the whole set instead of once per tile.
 */
export function registerTiles(tiles: HomeTile[]): void {
  registerHomeTiles(tiles);
}

/**
 * Register a single tile by ID from the default tile set.
 * Returns the unregister function.
 */
export function registerDefaultTile(tileId: string): (() => void) | undefined {
  const tile = ALL_TILES.find((t) => t.id === tileId);
  if (tile) {
    return registerHomeTile(tile);
  }
  return undefined;
}

/**
 * Get the dynamic tile definition by ID.
 */
export function getDynamicTile(tileId: string): HomeTile | undefined {
  return DYNAMIC_TILES.find((t) => t.id === tileId);
}
